import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PromoNotification = {
  id: number;
  playerGUID: string;
  type: string;
  transactionId?: string;
  heading: string;
  message: string;
  timestamp: number;
  isRead: number;
  isExpired: number;
  isClaimed: number;
};

export type PromotionGoals = {
  type?: string;
  weekly_hours_target?: number;
  monthly_hours_target?: number;
  total_hours_target?: number;
  weekly_achieved?: boolean;
  total_achieved?: boolean;
  [key: string]: any;
};

export type PromotionHours = {
  total: number;
  breakdowns?: any;
  progress?: {
    weekly_percentage: number;
    monthly_percentage: number;
    total_percentage: number;
  };
};

export type CashBackTier = { min_hours: number; credit_amount: number };

/** Weekly cash-back progress as the server computes it (EN-11293). The client shows these
 *  numbers; it does not map hours to tiers itself. */
export type CashBackProgress = {
  week_key: string | null;
  /** The current window's end (next reset day 00:00 CT) as a UTC ISO instant. */
  window_ends_at: string | null;
  this_week_hours: number;
  tiers: CashBackTier[];
  reached_tier: CashBackTier | null;
  next_tier: CashBackTier | null;
  hours_to_next_tier: number | null;
  weeks?: {
    week_key: string;
    hours: number;
    reached_tier: CashBackTier | null;
    closed: boolean;
  }[];
};

export type Promotion = {
  promo_id: number;
  format?: string;
  cash_back?: CashBackProgress;
  promo_name: string;
  promo_description: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  goals: PromotionGoals;
  reward: {
    type: string;
    target_type?: string;
    amount?: number;
    description?: string;
    [key: string]: any;
  };
  hours: PromotionHours;
  additional_rules?: any;
  player_facing_config?: any;
};

/**
 * The session's one login-modal slot (owner, 2026-09-24: the promo entry modal
 * and the multi-run introduction are never both presented — only one at a time).
 *   - 'none'     — no feature introduction wants this session; the promo behaves
 *                  exactly as it always has.
 *   - 'pending'  — the introduction may want it but the feature flags are still
 *                  evaluating (bounded, see featureFlags `useFeatureFlagsReady`);
 *                  the promo waits.
 *   - 'reserved' — the introduction holds this session. The promo sits it out
 *                  and returns on the next login, as it already does every login.
 */
export type FeatureIntroSlot = 'none' | 'pending' | 'reserved';

export type PlayerPromosState = {
  promotions: Promotion[];
  promoNotifications: PromoNotification[];
  // True while the auto-prompted Promo Entry Modal is open. The scrolling
  // notification ticker reads this to hold off rotating campaign messages until
  // the player dismisses the modal (so they aren't advanced behind it).
  promoEntryModalOpen: boolean;
  featureIntroSlot: FeatureIntroSlot;
  // The multi-run introduction's twin of promoEntryModalOpen, for the ticker.
  featureIntroModalOpen: boolean;
};

const initialState: PlayerPromosState = {
  promotions: [],
  promoNotifications: [],
  promoEntryModalOpen: false,
  featureIntroSlot: 'none',
  featureIntroModalOpen: false
};

export const playerPromosSlice = createSlice({
  name: 'playerPromos',
  initialState,
  reducers: {
    updatePlayerPromos: (state, action) => {
      if (action.payload?.promotions) {
        state.promotions = action.payload.promotions;
      }
      if (action.payload?.promoNotifications) {
        state.promoNotifications = action.payload.promoNotifications;
      }
    },
    markPlayerPromoNotificationRead: (state, action) => {
      const notification = state.promoNotifications.find((item) => item.id === action.payload);
      if (notification) {
        notification.isRead = 1;
      }
    },
    setPromoEntryModalOpen: (state, action) => {
      state.promoEntryModalOpen = !!action.payload;
    },
    setFeatureIntroSlot: (state, action: PayloadAction<FeatureIntroSlot>) => {
      state.featureIntroSlot = action.payload;
    },
    setFeatureIntroModalOpen: (state, action: PayloadAction<boolean>) => {
      state.featureIntroModalOpen = !!action.payload;
    }
  }
});

export const {
  updatePlayerPromos,
  markPlayerPromoNotificationRead,
  setPromoEntryModalOpen,
  setFeatureIntroSlot,
  setFeatureIntroModalOpen
} = playerPromosSlice.actions;

export default playerPromosSlice.reducer;
